import React, { useState, useEffect, useCallback } from 'react';
import {
    Heart, Zap, BookOpen, RefreshCw, CheckCircle, ArrowRight,
    Clock, ShieldCheck, BarChart2, Loader2, AlertTriangle,
    Star, FileText, Download, PenTool,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricCard from './MetricCard';
import { SectionHeader, Section, StatusBadge, EmptyMetric } from './OverviewTabV2';

/**
 * TeacherValueTab — "Are teachers getting value after lesson generation?"
 *
 * Shows lesson generation metrics, lesson journey funnel,
 * and session quality distribution.
 */

const TeacherValueTab = ({ filters = {} }) => {
    const [workflow, setWorkflow] = useState(null);
    const [genTime, setGenTime] = useState(null);
    const [journey, setJourney] = useState(null);
    const [quality, setQuality] = useState(null);
    const [workspace, setWorkspace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 7,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };

            const [wfRes, gtRes, jnRes, sqRes, wsRes] = await Promise.allSettled([
                adminAnalytics.getWorkflowAnalytics(params),
                adminAnalytics.getGenerationTime({ days: filters.days || 7 }),
                adminAnalytics.getLessonJourney(params),
                adminAnalytics.getSessionQuality({ days: filters.days || 7 }),
                adminAnalytics.getWorkspaceSummary(),
            ]);

            if (wfRes.status === 'fulfilled') setWorkflow(wfRes.value.data);
            if (gtRes.status === 'fulfilled') setGenTime(gtRes.value.data);
            if (jnRes.status === 'fulfilled') setJourney(jnRes.value.data);
            if (sqRes.status === 'fulfilled') setQuality(sqRes.value.data);
            if (wsRes.status === 'fulfilled') setWorkspace(wsRes.value.data);
        } catch (err) {
            setError(err.message || 'Failed to load teacher value data.');
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading teacher value metrics…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
            </div>
        );
    }

    const pipeline = workflow?.lesson_pipeline || {};

    // Session quality high-value percentage
    const highValueDist = (quality?.distribution || []).find(d => d.tier === 'high_value');
    const highValuePct = highValueDist?.pct ?? null;

    // Generation time
    const avgGenMs = genTime?.avg_ms;
    const avgGenTimeDisplay = avgGenMs != null ? `${(avgGenMs / 1000).toFixed(1)}s` : null;

    return (
        <div>
            <SectionHeader
                title="Teacher Value"
                subtitle="Are teachers getting value after lesson generation?"
            />

            {/* ── Lesson Generation Metrics ────────────────────────── */}
            <Section title="Lesson Generation" icon={Zap} color="#10b981">
                <MetricCard
                    label="Lessons Generated" value={pipeline.lessons_generated ?? 0}
                    icon={Zap} accent="#10b981"
                />
                <MetricCard
                    label="Avg Lessons / Teacher"
                    value={(() => {
                        const total = pipeline.lessons_generated ?? 0;
                        const users = workspace?.unique_teachers ?? workspace?.total_teachers;
                        if (total > 0 && users > 0) return (total / users).toFixed(1);
                        return '—';
                    })()}
                    icon={BookOpen} accent="#3b82f6"
                    sub={(() => {
                        const users = workspace?.unique_teachers ?? workspace?.total_teachers;
                        if (users > 0) return `Across ${users} active teachers`;
                        return 'Requires workspace teacher data';
                    })()}
                />
                <MetricCard
                    label="Pipeline Success Rate"
                    value={pipeline.success_rate_pct ?? '—'}
                    icon={ShieldCheck}
                    accent={pipeline.success_rate_pct >= 95 ? '#10b981' : '#ef4444'}
                    format="percent"
                />
                <MetricCard
                    label="Avg Generation Time"
                    value={avgGenTimeDisplay || '—'}
                    icon={Clock} accent="#8b5cf6"
                    sub={genTime?.sample_count
                        ? `${genTime.sample_count} samples · p50: ${genTime.p50_ms ? (genTime.p50_ms / 1000).toFixed(1) + 's' : '—'}`
                        : genTime?.telemetry_status === 'no_data'
                            ? 'Awaiting generation_completed events with duration_ms'
                            : null
                    }
                />
            </Section>

            {/* ── Post-Generation Value ────────────────────────────── */}
            <Section title="Post-Generation Value" icon={Heart} color="#ec4899">
                <MetricCard
                    label="Deliverable Rate"
                    value={workspace?.deliverable_rate != null ? `${Math.round(workspace.deliverable_rate * 100)}%` : '—'}
                    icon={FileText} accent="#10b981"
                />
                <MetricCard
                    label="Plan Tomorrow Rate"
                    value={workspace?.plan_tomorrow_rate != null ? `${Math.round(workspace.plan_tomorrow_rate * 100)}%` : '—'}
                    icon={RefreshCw} accent="#8b5cf6"
                />
                <MetricCard
                    label="Compliance Failure Rate"
                    value={workspace?.compliance_failure_rate != null ? `${Math.round(workspace.compliance_failure_rate * 100)}%` : '—'}
                    icon={ShieldCheck}
                    accent={workspace?.compliance_failure_rate > 0.1 ? '#ef4444' : '#10b981'}
                />
                {highValuePct != null ? (
                    <MetricCard
                        label="High-Value Session %"
                        value={highValuePct}
                        icon={Star} accent="#f59e0b" format="percent"
                    />
                ) : (
                    <EmptyMetric label="High-Value Session %" reason="no_data" />
                )}
            </Section>

            {/* ── Lesson Journey Funnel ────────────────────────────── */}
            <LessonJourneyFunnel data={journey} />

            {/* ── Session Quality Distribution ────────────────────── */}
            <SessionQualitySection data={quality} />
        </div>
    );
};


// ─── Lesson Journey Funnel ──────────────────────────────────────────────────

const JOURNEY_ICONS = {
    lesson_generated: Zap,
    lesson_saved: CheckCircle,
    lesson_revised: PenTool,
    pptx_downloaded: Download,
    student_packet_generated: FileText,
    quiz_generated: BookOpen,
    canvas_exported: ArrowRight,
    voiceover_generated: Star,
};

const LessonJourneyFunnel = ({ data }) => {
    if (!data) return null;

    const steps = data.steps || [];
    const baseline = data.baseline_lessons || 0;
    const maxCount = Math.max(...steps.map(s => s.count), 1);

    return (
        <div style={{ marginBottom: 32 }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 12, paddingBottom: 8,
                borderBottom: '2px solid #6366f120',
            }}>
                <ArrowRight size={15} style={{ color: '#6366f1' }} />
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Lesson Journey Funnel</h3>
                {data.data_model && (
                    <span style={{
                        fontSize: 9, fontWeight: 600, color: '#818cf8',
                        background: 'rgba(99,102,241,0.1)', padding: '2px 6px',
                        borderRadius: 4, fontFamily: 'monospace',
                    }}>
                        {data.data_model}
                    </span>
                )}
            </div>

            <div style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 14, padding: '20px 24px',
            }}>
                {steps.map((step, idx) => {
                    const Icon = JOURNEY_ICONS[step.step] || ArrowRight;
                    const barWidth = maxCount > 0 ? Math.max((step.count / maxCount) * 100, step.count > 0 ? 3 : 0) : 0;
                    const qualityColor = step.data_quality === 'strong' ? '#22c55e' : step.data_quality === 'growing' ? '#f59e0b' : '#94a3b8';

                    return (
                        <div key={step.step} style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: idx < steps.length - 1 ? 12 : 0 }}>
                            <div style={{
                                width: 28, height: 28, borderRadius: 8,
                                background: `${qualityColor}14`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <Icon size={13} style={{ color: qualityColor }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                                    <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{step.label}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {step.conversion_from_generated_pct != null && (
                                            <span style={{ fontSize: 10, color: '#94a3b8' }}>
                                                {step.conversion_from_generated_pct}%
                                            </span>
                                        )}
                                        <span style={{
                                            fontSize: 13, fontWeight: 700,
                                            color: step.count > 0 ? '#111827' : '#d1d5db',
                                        }}>
                                            {step.count}
                                        </span>
                                    </div>
                                </div>
                                <div style={{ height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                    <div style={{
                                        height: '100%', width: `${barWidth}%`,
                                        background: `linear-gradient(90deg, ${qualityColor}, ${qualityColor}80)`,
                                        borderRadius: 3, transition: 'width 0.4s ease',
                                    }} />
                                </div>
                                <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2, display: 'flex', gap: 6 }}>
                                    <span>{step.data_model}</span>
                                    <span style={{ color: qualityColor }}>● {step.data_quality}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {baseline === 0 && (
                    <div style={{
                        marginTop: 16, padding: '10px 14px', borderRadius: 8,
                        background: '#fffbeb', border: '1px solid #fde68a',
                        fontSize: 12, color: '#92400e',
                    }}>
                        No lessons in this period — journey will populate when teachers generate lessons.
                    </div>
                )}
            </div>
        </div>
    );
};


// ─── Session Quality Section ──────────────────────────────────────────────

const QUALITY_COLORS = {
    high_value: '#22c55e',
    productive: '#3b82f6',
    partial: '#f59e0b',
    shallow: '#f97316',
    abandoned: '#ef4444',
};

const SessionQualitySection = ({ data }) => {
    if (!data || data.total_sessions === 0) {
        return (
            <div style={{ marginBottom: 28 }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    marginBottom: 12, paddingBottom: 8,
                    borderBottom: '2px solid #f59e0b20',
                }}>
                    <BarChart2 size={15} style={{ color: '#f59e0b' }} />
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Session Quality</h3>
                </div>
                <div style={{
                    background: '#f9fafb', border: '1px solid #e5e7eb',
                    borderRadius: 14, padding: '24px', textAlign: 'center',
                }}>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: 0, fontWeight: 500 }}>
                        No session data available yet
                    </p>
                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0' }}>
                        Quality analysis requires workspace_events with session_id.
                        Sessions will appear as teachers use Saige with session tracking enabled.
                    </p>
                </div>
            </div>
        );
    }

    const distribution = data.distribution || [];
    const maxCount = Math.max(...distribution.map(d => d.count), 1);

    return (
        <div style={{ marginBottom: 28 }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 12, paddingBottom: 8,
                borderBottom: '2px solid #f59e0b20',
            }}>
                <BarChart2 size={15} style={{ color: '#f59e0b' }} />
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Session Quality</h3>
                <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 10,
                    background: '#f0f4f8', color: '#475569',
                }}>
                    {data.total_sessions} session{data.total_sessions !== 1 ? 's' : ''}
                </span>
            </div>

            <div style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 14, padding: '20px 24px',
            }}>
                {distribution.map((d, idx) => {
                    const color = QUALITY_COLORS[d.tier] || '#94a3b8';
                    const barWidth = maxCount > 0 ? Math.max((d.count / maxCount) * 100, d.count > 0 ? 2 : 0) : 0;

                    return (
                        <div key={d.tier} style={{ marginBottom: idx < distribution.length - 1 ? 14 : 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{
                                        width: 10, height: 10, borderRadius: '50%',
                                        background: color, display: 'inline-block',
                                    }} />
                                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{d.label}</span>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{d.description}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 14, fontWeight: 700, color }}>{d.count}</span>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{d.pct}%</span>
                                </div>
                            </div>
                            <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{
                                    height: '100%', width: `${barWidth}%`,
                                    background: color, borderRadius: 3,
                                    transition: 'width 0.4s ease', opacity: 0.8,
                                }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


export default TeacherValueTab;
